import React from 'react';
import { Box, Typography, Button, SxProps, Theme } from '@mui/material';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { useNavigate } from 'react-router-dom';
import { DashboardWidget as DashboardWidgetType } from '../../config/dashboard-widgets.config';

/**
 * DashboardWidget Component
 *
 * Reusable card component for dashboard widgets.
 * Design aligned with Stitch: Nexus Admin design system
 *
 * Features:
 * - Large icon with container background
 * - Title and description
 * - Action button with arrow
 * - Hover effects (scale, shadow)
 */
export interface DashboardWidgetProps {
  widget: DashboardWidgetType;
  variant?: 'default' | 'featured';
  sx?: SxProps<Theme>;
}

export const DashboardWidget: React.FC<DashboardWidgetProps> = ({
  widget,
  variant: _variant = 'default',
  sx = {},
}) => {
  const navigate = useNavigate();
  const Icon = widget.icon;

  const handleAction = () => {
    navigate(widget.actionPath);
  };

  return (
    <Box
      sx={{
        position: 'relative',
        overflow: 'hidden',
        backgroundColor: '#ffffff',
        border: '1px solid #c3c5d9',
        borderRadius: '12px',
        p: 4,
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        transition: 'box-shadow 0.3s',
        cursor: 'pointer',
        '&:hover': {
          boxShadow: '0 20px 25px -5px rgba(0,0,0,0.1), 0 10px 10px -5px rgba(0,0,0,0.04)',
          '& .widget-icon': {
            transform: 'scale(1.1)',
          },
          '& .action-arrow': {
            transform: 'translateX(4px)',
          },
        },
        ...sx,
      }}
    >
      {/* Icon */}
      <Box
        className="widget-icon"
        sx={{
          width: 56,
          height: 56,
          backgroundColor: '#dce9ff',
          borderRadius: '16px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          mb: 3,
          color: '#003ec7',
          transition: 'transform 0.3s',
        }}
      >
        <Icon sx={{ fontSize: 28 }} />
      </Box>

      {/* Content */}
      <Typography
        sx={{
          fontSize: '20px',
          fontFamily: 'Inter, sans-serif',
          fontWeight: 600,
          color: '#0b1c30',
          lineHeight: '28px',
          mb: 1,
        }}
      >
        {widget.title}
      </Typography>
      <Typography
        sx={{
          fontSize: '14px',
          fontFamily: 'Inter, sans-serif',
          color: '#434656',
          lineHeight: '20px',
          mb: 4,
          flex: 1,
        }}
      >
        {widget.description}
      </Typography>

      {/* Action Button */}
      <Button
        onClick={handleAction}
        aria-label={`${widget.actionLabel} for ${widget.title}`}
        sx={{
          alignSelf: 'flex-start',
          backgroundColor: '#003ec7',
          color: '#ffffff',
          fontSize: '13px',
          fontFamily: 'Inter, sans-serif',
          fontWeight: 600,
          px: 3,
          py: 1.5,
          borderRadius: '8px',
          textTransform: 'none',
          gap: 1,
          '&:hover': {
            backgroundColor: '#0052ff',
          },
          '&:active': {
            transform: 'scale(0.98)',
          },
        }}
      >
        {widget.actionLabel}
        <ArrowForwardIcon
          className="action-arrow"
          sx={{ fontSize: 16, transition: 'transform 0.2s' }}
        />
      </Button>
    </Box>
  );
};
